import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import actions from "../../../Store/actions";
import styles from "./styles";

import {
    Alert,
    Box,
    Button,
    List,
    ListItemButton,
    ListItemText,
} from "@mui/material/";
import UploadIcon from "@mui/icons-material/Upload";

import AppModal from "../AppModal";
import { useAssetTypeImageTypes } from "../../hooks";
import useImageTypes from "../PhotoList/useImageTypes";
import useImageType from "./useImageType";

const ImageArea = ({ imgIndex, assetMode, imageSrc, triggerCamera }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { assetDetails, typesList } = useSelector((state) => state);

    const {
        photos,
        assetTypeId,
        exclusionReasonTypeId,
        steps,
        activeStep,
        stepsCompleted,
    } = assetDetails;

    const [open, setOpen] = useState(false);

    const assetImageTypes = useAssetTypeImageTypes(assetTypeId);
    const imageTypes = useImageTypes(assetImageTypes, exclusionReasonTypeId);
    const [imageTypeId, setImageTypeId] = useImageType(imgIndex, imageTypes);

    const type = imageTypes.find((item) => item.id === imageTypeId);
    let imageTypeName = type ? type.name : "";

    // for asset exclusion, we don't want to show default imageTypeName - only when it is set
    // also if image is not taken, don't show label
    imageTypeName =
        (assetMode === "exclude" && !exclusionReasonTypeId) || !photos[imgIndex]
            ? ""
            : imageTypeName;

    const disableUpload = assetMode === "exclude" && !exclusionReasonTypeId;

    // map exclusion reason with image type
    useEffect(() => {
        if (exclusionReasonTypeId) {
            const typeObj = typesList.exclusionImageTypes.data.find(
                (item) => item.exclusionReasonId == exclusionReasonTypeId
            );

            if (typeObj && Object.keys(typeObj).length > 0)
                setImageTypeId(typeObj.imageTypeId);
        }
    }, [exclusionReasonTypeId, imageTypes]);

    //On exclusionReason Image type change
    useEffect(() => {
        if (imageTypeName.length > 0) {
            if (activeStep) {
                const updSteps = [...steps];
                const updPhotos = [...photos];
                if (stepsCompleted[activeStep]) {
                    updPhotos[activeStep - 1].imageType = type.id;
                    updSteps[activeStep].label = imageTypeName;
                }
                dispatch(
                    actions.assetDetails.create.set({
                        steps: updSteps,
                        photos: updPhotos,
                    })
                );
            }
        }
    }, [exclusionReasonTypeId, imageTypeName, activeStep]);

    const hdlItemSelection = (imgTypeId) => () => {
        setImageTypeId(imgTypeId);

        if (photos[imgIndex]) {
            const updPhotos = [...photos];
            updPhotos[imgIndex].imageType = imgTypeId;
            dispatch(actions.assetDetails.create.set({ photos: updPhotos }));
        }
        setOpen(false);
    };

    const hdlSelection = () => {
        // for asset exclude, we do not allow to upload image until exclusion reason is set
        if (disableUpload) return;
        setOpen(true);
    };

    return (
        <>
            <Box sx={styles.wrapImage}>
                <Button
                    variant="text"
                    disabled={disableUpload}
                    startIcon={<UploadIcon sx={styles.uploadIcon} />}
                    onClick={disableUpload ? null : triggerCamera}
                    sx={styles.uploadText}
                >
                    {t("takeAPicture")}
                </Button>
                {Boolean(imageSrc) && (
                    <img src={imageSrc} alt="photo" style={styles.image} />
                )}
            </Box>
            {imageSrc && imageTypeName && (
                <Button variant="text" fullWidth onClick={hdlSelection}>
                    {imageTypeName}
                </Button>
            )}
            <AppModal
                isVisible={open}
                title={t("imageType")}
                onClose={() => setOpen(false)}
            >
                <List
                    sx={styles.list}
                    component="nav"
                    aria-labelledby="nested-list-subheader"
                >
                    {imageTypes.map((item) => {
                        return (
                            <ListItemButton
                                key={item.id}
                                sx={styles.listItemBtn}
                                onClick={hdlItemSelection(item.id)}
                            >
                                <ListItemText primary={item.name} />
                            </ListItemButton>
                        );
                    })}
                </List>
            </AppModal>
            {imageSrc && imageTypeName && (
                <Alert severity="info">{t("messages.exclusionReason")}</Alert>
            )}
        </>
    );
};

export default ImageArea;
